<script lang="ts">
  import '../app.css';
  import { onMount } from 'svelte';
  import { onNavigate } from '$app/navigation';
  import { theme } from '$stores/theme.svelte';
  import { ticker } from '$stores/ticker.svelte';
  import { ui } from '$stores/ui.svelte';
  import type { Snippet } from 'svelte';

  interface Props {
    children?: Snippet;
  }
  let { children }: Props = $props();

  $effect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-theme', theme.current);
    document.documentElement.setAttribute('data-mode', theme.resolvedMode);
  });

  onNavigate(() => {
    const svt = document.startViewTransition;
    if (!svt) return;
    if (theme.current === 'brutalist') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    return new Promise<void>((fulfil) => {
      svt.call(
        document,
        () =>
          new Promise<void>((resolve) => {
            fulfil();
            resolve();
          })
      );
    });
  });

  // Mount once: start ticker, attach blur listeners for one-glance reveal
  onMount(() => {
    ticker.start();

    const onBlur = () => ui.setGlobalHidden(true);
    const onFocus = () => ui.setGlobalHidden(false);
    const onVisibility = () => {
      if (document.hidden) ui.setGlobalHidden(true);
      else ui.setGlobalHidden(false);
    };
    const onToggleTheme = () => theme.toggle();

    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('vuauth:toggle-theme', onToggleTheme);

    // Console banner — respect the silence
    // Skip the banner inside iframes (e.g. the /demo embeds) so it doesn't print 3×.
    if (typeof console !== 'undefined' && window === window.top) {
      console.log('%cVuAuth — local', 'font: 700 14px/1 ui-sans-serif; color: #00d4ff');
      console.log('%cnetwork calls: 0 · third-party origins: 0 · telemetry: 0', 'color: #888');
      console.log(
        '%c"YOUR data. YOUR device. YOUR control."',
        'font-style: italic; color: #00d4ff'
      );
    }

    return () => {
      ticker.stop();
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('vuauth:toggle-theme', onToggleTheme);
    };
  });
</script>

{@render children?.()}
